import { useSyncExternalStore } from "react";
import { api, fmtNumber } from "@/lib/api";

// v80 — one plain reason per missing bot check; never a number, a rule or a cut-off.
export const WHY = {
  yt_small: "Small audience. The check starts once the channel grows.",
  yt_history: "Not enough video history yet. This updates automatically.",
  live_none: "Does not go live.",
  live_unmeasured: "No live stream measured yet.",
  live_small: "Small live audience. The check starts once live viewership grows.",
  live_more: "Needs a few more measured streams. This updates automatically.",
  tg_none: "No Telegram channel found.",
  tg_personal: "Personal account, not a channel.",
  tg_group: "A group: groups do not show views, so there is nothing to check.",
  tg_unreadable: "Posts cannot be read (the channel preview is off).",
  tg_small: "Small channel. The check starts once it grows.",
  tg_unchecked: "Not checked yet.",
  ig_none: "No Instagram handle found.",
  ig_reels: "Reels could not be read.",
  ig_small: "Small account. The check starts once it grows.",
  ig_unchecked: "Not checked yet.",
};

// "Engagement looks bought": the estimate is blank although the check is Medium or High.
const BOUGHT = {
  ig: { high: "Their likes look bought. Go by reel views, not by likes or followers.", medium: "Their likes look high for their views. Treat likes and engagement with caution." },
  tg: { high: "Their views look bought. Go by reactions, not by views or subscribers.", medium: "Their views look high for their size with almost no reactions. Treat the view count with caution." },
};

export const LEVEL_WORD = { none: "No sign", low: "Low", medium: "Medium", high: "High", small: "Small audience", not_checked: "Not checked yet" };
export const LEVEL_COLOR = { high: "#F43F5E", medium: "#FB923C", low: "#A1A1AA", none: "#39FF14", small: "#71717A", not_checked: "#52525B", missing: "#3F3F46" };
export const BOT_PLATFORM = { yt: "YouTube", live: "YouTube live", tg: "Telegram", ig: "Instagram" };

const short = (n) => (n >= 1e6 ? `${Number((n / 1e6).toFixed(1))}M` : n >= 1e3 ? `${Number((n / 1e3).toFixed(n >= 1e4 ? 0 : 1))}K` : fmtNumber(Math.round(n)));
const times = (x) => `${x >= 2 ? Math.round(x) : Number(x.toFixed(1))}×`;

export function bought(av, b) {
  return b?.bought ? BOUGHT[av]?.[b.level] || null : null;
}

// The "likely real" line for a Medium / High check (Instagram, Telegram, live).
export function estimateLine(av, b) {
  const e = b?.est;
  if (!e || !(b.level === "high" || b.level === "medium")) return null;
  const by = e.x ? ` (likely inflated by about ${times(e.x)})` : "";
  if (av === "live") return `Likely real live viewers: about ${fmtNumber(e.lo)}–${fmtNumber(e.hi)}${by}.`;
  const unit = av === "ig" ? "followers" : "subscribers";
  return `Likely real audience: about ${short(e.real)}${e.total ? ` of their ${short(e.total)} ${unit}` : ""}${by}.`;
}

export const levelWord = (b) => (b?.level === "missing" ? "Not found" : LEVEL_WORD[b?.level] || "Not checked yet");

// One short text for the list chip hover and the Best avenues line.
export function botLineText(av, b) {
  if (!b) return null;
  const extra = b.why ? WHY[b.why] : bought(av, b) || estimateLine(av, b);
  return `${levelWord(b)}${extra ? ` · ${extra}` : ""}`;
}

// v80 — Creator 360 bot checks a client opened in this session (kept in memory only).
const OPENED = new Map();
const subs = new Set();
const emit = () => subs.forEach((f) => f());
let version = 0;
export function useOpenedBots() {
  return useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => version);
}
export const openedBots = (id) => OPENED.get(id);
export async function openBots(id) {
  const { data } = await api.post("/bot-check/open", { ids: [id], place: "c360" });
  const b = data?.c360?.[id];
  if (b) { OPENED.set(id, b); version += 1; emit(); }
  return b;
}
